import React, { useCallback, useEffect, useState } from 'react'
import { Grid, Box, Typography, LinearProgress, IconButton, Tooltip, MenuItem, Menu, Divider, Button, Stack } from '@mui/material'
import { withStyles } from '@mui/styles'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined'
import ListAltIcon from '@mui/icons-material/ListAlt'
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined'
import CancelIcon from '@mui/icons-material/Cancel'
import ScheduleIcon from '@mui/icons-material/Schedule'
import MuiChip from '@mui/material/Chip'
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined'
import BallotIcon from '@mui/icons-material/Ballot'
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { ClassNameMap } from '@mui/material/styles'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import stylesheet from '../detail-container.stylesheet'
import { RootState, useAppThunkDispatch } from '../../../../redux/store'
import Helper from '../../../../utils/helper'
import { setSelectedProgram, setSelectedTask, incrementProgramFilterVersion } from '../../../../redux/common-data-slice'
import { updateEnrollments } from '../../../../redux/patient-detail-slice'
import { PatientService } from '../../../../services/patient.service'
import { checkCondition } from '../common/condition-check'
import ConfirmationDialogComponent from '../common/confirmation-dialog.component'
import AddLifestyleDialogComponent from './add-lifestyle.component'
import AddMonitorComponent from './add-monitor.component'
import PendingItemsDialogComponent from './pending-items.component'
import OverrideItemsComponent from './override-items.component'
import { useUpdateDataAction } from '../../../../utils/update-details-hook'
import { useThemeMode } from '../../../../contexts/ThemeContext'
import { adjustColorForDarkMode, getContrastTextColor } from '../../../../utils/color-utils'

interface IProps {
  handleCloseOutNote: any
  classes: ClassNameMap
}

const SubHeaderComponent: React.FC<any> = ({ handleCloseOutNote, classes }) => {
  const dispatch = useAppThunkDispatch()
  const { mode } = useThemeMode()
  const isDarkMode = mode === 'dark'
  const [data, setData] = useState([] as any)
  const [loading, setLoading] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [clickedProgram, setClickedProgram] = useState('' as any)
  const [isMonitor, setIsMonitor] = useState(false)
  const [isLifestyle, setIsLifestyle] = useState(false)
  const [prgTasks, setPrgTasks] = useState({ isTask: false, program: '' }) as any
  const [monitorData, setMonitorData] = useState([] as any)
  const [lifestyleData, setLifestyleData] = useState([] as any)
  const [isActionItem, setIsActionItem] = useState(false)
  const [prgStatus, setPrgStatus] = useState(null as any)
  const [isPendingItem, setIsPendingItem] = useState(false)
  const patientService = new PatientService()
  const [removePrgEnrollment, setRemovePrgEnrollment] = useState(false)
  const patientDetail: any = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const { updatePatientData } = useUpdateDataAction(patientDetail.id)
  const patientDetailStatus: any = useSelector((state: RootState) => state.patientDetail.status)
  const selectedProgram = useSelector((state: RootState) => state.commonData.selectedProgram)
  const multiProgramConfirm = useSelector((state: RootState) => state.multiProgramConfirm)
  const stageChange = useSelector((state: RootState) => state.stageChange.loading)
  const selectedTask = useSelector((state: RootState) => state.commonData.selectedTask)

  const Chip = withStyles((theme) => ({
    root: {
      padding: theme.spacing(1),
      border: 'none',
      borderRadius: '4px',
    },
    labelSmall: {
      width: '100%',
    },
  }))(MuiChip)

  const onChipClick = (item: string) => {
    const toggleSelected = (selected: boolean) => {
      dispatch(updateEnrollments(
        patientDetail.program_enrollments.map((e: any) =>
          e.program === item ? { ...e, selected } : e
        )
      ))
    }

    if (selectedProgram.includes(item)) {
      if (selectedProgram.length !== 1) {
        dispatch(setSelectedProgram(selectedProgram.filter((f: any) => f !== item)))
        toggleSelected(false)
        dispatch(incrementProgramFilterVersion())
      }
    } else {
      dispatch(setSelectedProgram([...selectedProgram, item]))
      toggleSelected(true)
      dispatch(incrementProgramFilterVersion())
    }
  }

  const handleClose = () => setAnchorEl(null)

  useEffect(() => {
    if (isMonitor) {
      patientService
        .getMonitorList()
        .then((response: any) => {
          setMonitorData(response.data)
        })
        .catch((error) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
    }
  }, [isMonitor])

  useEffect(() => {
    if (isLifestyle) {
      patientService
        .getLifestylesList()
        .then((response: any) => {
          setLifestyleData(response.data)
        })
        .catch((error) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
    }
  }, [isLifestyle])

  const handlePrgStatusConfirmClose = () => setPrgStatus(null)

  const handlePrgStatus = (status: any) => {
    setAnchorEl(null)
    setPrgStatus(status)
  }
  const getPendingTaskCount = (prg: any, status: any) => {
    const count = (Object.keys(patientDetail || {}).length > 0 && patientDetail?.pending_task_counts && patientDetail?.pending_task_counts[prg]) || 0
    return (
      <Typography variant="body2" className={classes.srcAttributes} color={status === 'inactive' ? 'var(--color-text-muted) !important' : 'inherit'}>
        {status === 'inactive' ? count || '-' : <strong>{count}</strong>}
      </Typography>
    )
  }

  const confirmPrgStatus = () => {
    const status = prgStatus || null
    if (patientDetail.program_enrollments?.find((f: { program: any }) => f.program === clickedProgram)?.status !== status.toLocaleLowerCase()) {
      setLoading(true)
      patientService
        .changeProgramStatus(patientDetail.id, clickedProgram, status)
        .then(() => {
          setLoading(false)
          setPrgStatus(null)
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        })
        .catch((error: any) => {
          setLoading(false)
          setPrgStatus(null)
          toast.error(Helper.getErrorText(error), Helper.bottom_center())
        })
    }
  }

  const isEvalProgram = (prg: any) => patientDetail?.program_enrollments?.some((m: any) => m.program_type === 'evaluation' && m.selected && m.program === prg)

  const handleMonitorSubmit = (currMonitor: any) => {
    setIsMonitor(false)

    const enrollmentId = getEnrollmentId()
    if (enrollmentId) {
      patientService
        .createMonitor(enrollmentId, currMonitor)
        .then((response: any) => {
          if (response.status === 200) {
            updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: false })
          }
        })
        .catch((error) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
    }
  }

  const getEnrollmentId = () => (clickedProgram && patientDetail?.program_enrollments.filter((m: { program: any }) => m.program === clickedProgram)?.[0]?.enrollment_id) || null

  const handleLifestyleSubmit = (currLifestyle: any) => {
    setIsLifestyle(false)
    const enrollmentId = getEnrollmentId()
    if (enrollmentId) {
      patientService
        .createLifestyle(enrollmentId, currLifestyle)
        .then((response: any) => {
          if (response.status === 200) {
            updatePatientData({ isTreatments: true })
          }
        })
        .catch((error) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
    }
  }

  const handleMonitorDialogClose = () => setIsMonitor(false)
  const handleLifestyleDialogClose = () => setIsLifestyle(false)
  const handleActionDialogClose = () => setIsActionItem(false)
  const handlePendingDialogClose = () => setIsPendingItem(false)

  const handleActionItem = () => {
    setIsActionItem(true)
    handleClose()
  }

  const handleOverrideItem = () => {
    setIsPendingItem(true)
    handleClose()
  }

  const handleCloseNote = () => {
    handleClose()
    handleCloseOutNote(clickedProgram)
  }

  const handleAddLifestyle = () => {
    setIsLifestyle(true)
    handleClose()
  }

  const handleAddMonitor = () => {
    setIsMonitor(true)
    handleClose()
  }

  const handleRemoveEnrolment = () => {
    setRemovePrgEnrollment(true)
    handleClose()
  }

  const renderProgramMenu = (title: any, status: any, handleClick: any) => {
    return (
      <MenuItem
        onClick={patientDetailStatus === 'loading' || status === 'inactive' ? undefined : handleClick}
        className={status === 'inactive' ? classes.subTitleDisabled : classes.subTitle}
        disabled={patientDetailStatus === 'loading' || status === 'inactive'}
      >
        {title}
      </MenuItem>
    )
  }

  const getPrgActionList = () => {
    const isEvaluationPrg = Helper.isEvaluationPrg(patientDetail?.program_enrollments || [])
    const isTreatmentPrg = Helper.isTreatmentPrg(patientDetail?.program_enrollments || [])

    if (isEvaluationPrg && !isTreatmentPrg) {
      return [
        { title: 'Pending items', callback: handleActionItem },
        { title: 'Override measurements', callback: handleOverrideItem },
        { title: 'Start enrollment', callback: setProgramStage },
      ]
    } else {
      return [
        { title: 'Add lifestyle plan', callback: handleAddLifestyle },
        { title: 'Add monitor', callback: handleAddMonitor },
        { title: 'Pending items', callback: handleActionItem },
        { title: 'Override measurements', callback: handleOverrideItem },
        { title: 'Start enrollment', callback: setProgramStage },
        { title: 'Generate closeout note', callback: handleCloseNote },
      ]
    }
  }

  const setPostDiagnosis = () => {
    setAnchorEl(null)
    if (!patientDetail?.id || !clickedProgram) return
    setLoading(true)
    patientService
      .changeStageByName(patientDetail.id, clickedProgram, 'post-diagnosis')
      ?.then((response: any) => {
        if (response?.data) {
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        }
      })
      .catch((error: any) => {
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const setProgramStage = async () => {
    setAnchorEl(null)
    if (!patientDetail?.id || !clickedProgram) return

    setLoading(true)
    if (isEvalProgram(clickedProgram)) {
      patientService
        .changeStageByName(patientDetail.id, clickedProgram, 'screening')
        ?.then((response: any) => {
          if (response?.data) {
            updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
          }
        })
        .catch((error: any) => {
          toast.error(Helper.getErrorText(error), Helper.bottom_center())
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      const enrollment = patientDetail?.program_enrollments?.find((e: any) => e.program === clickedProgram)
      const nextStage = enrollment?.current_stage === 'enrollment' ? 'data_intake' : 'maintenance_surveillance'

      patientService
        .changeStageByName(patientDetail.id, clickedProgram, nextStage)
        ?.then((response: any) => {
          if (response?.data) {
            updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
          }
        })
        .catch((error: any) => {
          toast.error(Helper.getErrorText(error), Helper.bottom_center())
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setClickedProgram(event.currentTarget.getAttribute('data-program'))
    setAnchorEl(event.currentTarget)
    // dispatch(setSelectedTask({ prg: event.currentTarget.getAttribute('data-program'), isUpdate: true }))
  }

  const getLable = (e: any) => {
    return (
      <Stack direction={'row'} alignItems="center" spacing={2} sx={{ backgroundColor: 'transparent' }}>
        <Typography
          variant="body2"
          className={classes.srcAttributes}
          color={e.status === 'inactive' ? 'var(--color-text-muted) !important' : 'inherit'}
          component="span"
          sx={{ backgroundColor: 'transparent' }}
        >
          {e.status === 'inactive' ? <>{`${e.program_display_name} - `}</> : <strong>{e.program_display_name} - </strong>}
          <span>{stageChange === false ? Helper.stageName(e.current_stage) : '...'}</span>
        </Typography>
        <Typography
          className={classes.srcAttributes}
          variant="body2"
          color={e.status === 'inactive' ? 'var(--color-text-muted) !important' : 'inherit'}
          component="span"
          sx={{ backgroundColor: 'transparent' }}
        >
          {e.status === 'inactive' && patientDetailStatus !== 'loading' ? (
            <>{multiProgramConfirm.find((m) => m.program === e.program)?.pending || '0'}</>
          ) : (
            <strong>{multiProgramConfirm.find((m) => m.program === e.program)?.pending || '0'}</strong>
          )}
        </Typography>
      </Stack>
    )
  }

  useEffect(() => {
    const stage = patientDetail?.program_enrollments?.map((e: any) => {
      return { ...e }
    })
    setData(stage)
  }, [patientDetail])

  const createCase = (program: string, enrollId: string) => {
    setLoading(true)
    patientService
      .createCase(patientDetail.id, program)
      .then((response: any) => {
        setLoading(false)
        if (response?.data?.success?.case_number) {
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        } else if (response?.data?.cases[0].ref === 'DUPLICATE_PATIENT_CASE') {
          toast.info(`Case already exists on Salesforce, case number ${response.data.cases[0].case_number}`, {
            position: 'bottom-center',
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
          })
        }
        patientService.deepLink(patientDetail.id, enrollId).then((res: any) => {
          setData(data.map((el: any) => (el.enrollment_id === enrollId ? { ...el, sf_deeplink: res.data.deeplink_uri } : el)))
        })
      })
      .catch((error: any) => {
        setLoading(false)
        if (error.response) {
          toast.error(Helper.getErrorText(error), {
            position: 'bottom-center',
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
          })
        }
      })
  }

  const getSFCaseStatus = (program: string, enrollId: string) => {
    setLoading(true)
    patientService
      .getSFCaseStatus(patientDetail.id, program)
      .then((response: any) => {
        setLoading(false)
        if (response?.data) {
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        }
      })
      .catch((error: any) => {
        setLoading(false)
        if (error.response) {
          toast.error(Helper.getErrorText(error), Helper.bottom_center())
        }
      })
  }

  const setIconBasedonStatus = useCallback(
    (_item: any) => {
      if (selectedProgram.includes(_item.program)) {
        if (_item.status === 'active') {
          return <CheckCircleIcon className={classes.programTargetVisibilityIcon} />
        } else if (_item.status === 'inactive') {
          return <CancelIcon className={classes.programTargetVisibilityIcon} />
        } else if (_item.status === 'temporarily_inactive') {
          return <RemoveCircleOutlineOutlinedIcon className={classes.programTargetVisibilityIcon} />
        }
      } else {
        return <RadioButtonUncheckedOutlinedIcon className={classes.programTargetVisibilityIcon} />
      }
    },
    [selectedProgram]
  )

  const renderCaseStatusIcon = (status: any, program: any, color_code: any, sf_deeplink: any, enrollment_id: any) => {
    let IconType
    let colorCode = '#00c853'

    if (sf_deeplink === null) {
      IconType = AddCircleOutlinedIcon
      colorCode = '#bdbdbd'
    } else if (status === 'ESCALATED') {
      IconType = BallotIcon
      colorCode = '#FF0000'
    } else if (status === 'CLOSED') {
      IconType = BallotIcon
      colorCode = '#050505'
    } else IconType = BallotIcon

    const pendingCount = multiProgramConfirm.find((m) => m.program === program)?.pending || 0
    return (
      <Grid container className={classes.programIcons}>
        <Grid item>
          <Tooltip title={'Sync salesforce case status'} placement="bottom-start">
            <IconButton
              disabled={!selectedProgram.includes(program) || patientDetailStatus === 'loading'}
              className={classes.subHeaderStatusIcon}
              onClick={patientDetailStatus === 'loading' ? undefined : () => getSFCaseStatus(program, enrollment_id)}
            >
              <ScheduleIcon sx={{ padding: '0rem 0.1rem 0 0.2rem', fill: 'var(--color-text-secondary) !important', fontSize: '1.45rem' }} />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid item>
          <Tooltip title={sf_deeplink === null ? 'Create case' : `View ${checkCondition(status !== 'NOT_AVAILABLE', Helper.toCamelCase(status), '')} case`} placement="bottom-start">
            <IconButton
              disabled={!selectedProgram.includes(program) || patientDetailStatus === 'loading'}
              className={classes.subHeaderStatusIcon}
              onClick={
                patientDetailStatus === 'loading'
                  ? undefined
                  : () => {
                      if (sf_deeplink === null) createCase(program, enrollment_id)
                    }
              }
              href={sf_deeplink}
              target="same"
            >
              <IconType
                style={{
                  fill: `${selectedProgram.includes(program) ? colorCode : '#CBC7C780'}`,
                  fontSize: '24px',
                }}
              />
            </IconButton>
          </Tooltip>
        </Grid>
        {status !== 'CLOSED' && pendingCount === 0 && (
          <Grid item>
            <Tooltip title={sf_deeplink === null ? 'Case is not available' : 'Case is not closed'} placement="bottom-start">
              <WarningOutlinedIcon className={classes.warningIcon} />
            </Tooltip>
          </Grid>
        )}
      </Grid>
    )
  }

  const handleConfirmClose = () => setRemovePrgEnrollment(false)

  const confirmStatus = () => {
    setRemovePrgEnrollment(false)
    const eId = data.filter((el: any) => el.program === clickedProgram)[0]?.enrollment_id || ''
    clickedProgram &&
      patientService
        .removeEnrollment(patientDetail.id, eId)
        .then((_res: any) => {
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        })
        .catch((error) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
  }

  const getActionTitle = () => {
    const prgObj = patientDetail.program_enrollments?.find((f: { program: any }) => f.program === clickedProgram) || {}
    return `${prgObj.program_display_name || clickedProgram} - ${Helper.stageName(prgObj?.current_stage)}`
  }

  const handleToxicities = () => {
    handleClose()
    setLoading(true)
    clickedProgram &&
      patientService
        .runToxicities(patientDetail.id, { programs: [clickedProgram] })
        .then((_res: any) => {
          setLoading(false)
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        })
        .catch((error: any) => {
          setLoading(false)
          toast.error(Helper.getErrorText(error), Helper.bottom_center())
        })
  }

  const handleTasks = (prg: any) => {
    setClickedProgram(prg)
    setPrgTasks({ isTasks: true, program: prg })
    dispatch(setSelectedTask({ prg: prg, isUpdate: true }))
  }

  return (
    <>
      {!isActionItem && (stageChange === true || ['loading', 'dia_loading', 'refreshing'].includes(patientDetailStatus) || loading) && <LinearProgress className={classes.fullWidth} />}
      <Box className={classes.subHeaderContainerBg}>
        <Grid container spacing={1} direction="row">
          {data
            ?.sort((a: any, b: any) => (a.program_display_name.toLowerCase() > b.program_display_name.toLowerCase() ? 1 : -1))
            ?.map((_e: any) => {
              // Adjust color for dark mode
              const adjustedColor = adjustColorForDarkMode(_e.color_code, isDarkMode)
              const textColorType = getContrastTextColor(adjustedColor)
              const chipTextColor = textColorType === 'light' ? '#ffffff' : '#000000'

              return (
                <div key={`subheader-${_e.program}`} className={classes.subHeaderData}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      icon={setIconBasedonStatus(_e)}
                      clickable={patientDetailStatus !== 'loading'}
                      disabled={patientDetailStatus === 'loading'}
                      onClick={() => patientDetailStatus !== 'loading' && onChipClick(_e.program)}
                      sx={
                        selectedProgram.includes(_e.program)
                          ? {
                              width: '100%',
                              backgroundColor: `${adjustedColor} !important`,
                              height: '1.40rem',
                              borderRadius: '4px 0 0 4px',
                              cursor: _e.status === 'inactive' ? 'default' : 'pointer',
                              border: `${_e.status === 'inactive' ? '0.09rem dotted grey' : adjustedColor}`,
                              marginLeft: '2px',
                              padding: '5px 0px 5px 5px',
                              boxShadow: _e.status !== 'inactive' ? `-3px 0px 5px -3px ${adjustedColor}, 0px 3px 5px 1px rgba(0,0,0,0.14), 0px 3px 5px 2px rgba(0,0,0,0.12)` : 'none',
                              '& .MuiChip-label': {
                                backgroundColor: 'transparent !important',
                              },
                              '& .MuiTypography-root': {
                                color: `${chipTextColor} !important`,
                              },
                              '&:hover': {
                                backgroundColor: `${adjustedColor} !important`,
                              },
                            }
                          : {
                              backgroundColor: 'var(--color-paper) !important',
                              height: '1.40rem',
                              borderRadius: '4px 0 0 4px',
                              marginLeft: '2px',
                              padding: '5px 0px 5px 5px',
                              border: '1px solid var(--color-paper)',
                              '& .MuiChip-label': {
                                backgroundColor: 'transparent !important',
                              },
                              '&:hover': {
                                backgroundColor: `${adjustedColor} !important`,
                              },
                            }
                      }
                      label={getLable(_e)}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      size="small"
                      disabled={patientDetailStatus === 'loading' || !selectedProgram.includes(_e.program)}
                      sx={
                        selectedProgram.includes(_e.program)
                          ? {
                              backgroundColor: adjustedColor,
                              color: chipTextColor,
                              height: '1.40rem',
                              borderRadius: '0 4px 4px 0',
                              borderLeft: 'none !important',
                              border: `${_e.status === 'inactive' ? '0.09rem dotted grey' : adjustedColor}`,
                              '&:hover': {
                                backgroundColor: adjustedColor,
                              },
                            }
                          : {
                              backgroundColor: 'var(--color-paper)',
                              height: '1.40rem',
                              borderRadius: '0 4px 4px 0',
                              '&:hover': {
                                backgroundColor: 'var(--color-paper)',
                              },
                            }
                      }
                      aria-controls={`simple-menu-${_e.program}`}
                      data-program={_e.program}
                      aria-haspopup="true"
                      onClick={(e: any) => patientDetailStatus !== 'loading' && handleClick(e)}
                    >
                      <ArrowDropDownIcon />
                    </IconButton>
                    {clickedProgram === _e.program && (
                      <Menu
                        id={`simple-menu-${_e.program}`}
                        anchorEl={anchorEl}
                        keepMounted
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        sx={{
                          '.MuiPaper-root': {
                            boxShadow: `-3px 5px 5px -3px ${adjustedColor}, 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)`,
                          },
                          '.MuiMenuItem-root': {
                            padding: '0.28rem 0.8rem !important',
                          },
                        }}
                      >
                        {getPrgActionList().map((l: any) => (
                          <>
                            {l.title === 'Start enrollment' && (!isEvalProgram(_e.program) || (isEvalProgram(_e.program) && _e.current_stage === 'enrollment')) && (
                              <Divider sx={{ margin: '0px 0.3rem !important', paddingBottom: '0.2rem !important' }} />
                            )}
                            {isEvalProgram(_e.program) ? (
                              <>
                                {!(l.title === 'Start enrollment' && _e.current_stage !== 'enrollment') &&
                                  renderProgramMenu(l.title === 'Start enrollment' && _e.current_stage === 'enrollment' ? 'Start screening' : l.title, _e.status, l.callback)}
                              </>
                            ) : (
                              !(l.title === 'Start enrollment' && _e.current_stage === 'maintenance_surveillance') &&
                              renderProgramMenu(
                                l.title === 'Start enrollment' && _e.current_stage !== 'maintenance_surveillance'
                                  ? _e.current_stage === 'enrollment'
                                    ? 'Start data intake'
                                    : 'Start maintenance'
                                  : l.title,
                                _e.status,
                                l.callback
                              )
                            )}
                          </>
                        ))}
                        {_e.program === 'amyloidosis_evaluation' && _e.current_stage !== 'post-diagnosis' && (
                          <>
                            <Divider sx={{ margin: '0px 0.3rem !important', paddingBottom: '0.2rem !important' }} />
                            {renderProgramMenu('Set Post-Diagnosis', _e.status, setPostDiagnosis)}
                          </>
                        )}
                        <Divider sx={{ margin: '0px 0.3rem !important', paddingBottom: '0.2rem !important' }} />
                        {patientDetail?.program_enrollments?.find((f: { program: any }) => f.program === clickedProgram)?.status?.toLowerCase() === 'active' ? (
                          <MenuItem
                            onClick={patientDetailStatus === 'loading' ? undefined : () => handlePrgStatus('INACTIVE')}
                            className={classes.subTitle}
                            disabled={patientDetailStatus === 'loading'}
                          >
                            Make inactive
                          </MenuItem>
                        ) : (
                          <MenuItem onClick={patientDetailStatus === 'loading' ? undefined : () => handlePrgStatus('ACTIVE')} className={classes.subTitle} disabled={patientDetailStatus === 'loading'}>
                            Make active
                          </MenuItem>
                        )}
                        {renderProgramMenu('Remove enrollment', _e.status, handleRemoveEnrolment)}
                      </Menu>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {patientDetail?.use_task_management ? (
                      <Box className={classes.programIcons} pl={0.5}>
                        <Tooltip title={'Tasks'} placement="bottom">
                          <Button
                            startIcon={
                              <ListAltIcon
                                fontSize="small"
                                sx={{
                                  fill: (!selectedProgram.includes(_e.program) || _e.status === 'inactive') 
                                    ? 'var(--color-text-muted)' 
                                    : 'var(--color-text-secondary) !important',
                                }}
                              />
                            }
                            disabled={!selectedProgram.includes(_e.program) || patientDetailStatus === 'loading' || _e.status === 'inactive'}
                            onClick={data?.length < 2 || patientDetailStatus === 'loading' ? undefined : () => handleTasks(_e.program)}
                            sx={{
                              color: chipTextColor,
                              minWidth: '2rem',
                              height: '1.40rem',
                              backgroundColor: _e.status === 'inactive' ? 'var(--color-paper)' : selectedProgram.includes(_e.program) ? adjustedColor : '',
                              '&.Mui-disabled': {
                                 border: 'none !important',
                              },
                              ':hover': {
                                backgroundColor: _e.status === 'inactive' ? 'var(--color-paper)' : selectedProgram.includes(_e.program) ? adjustedColor : '',
                              },
                            }}
                          >
                            {getPendingTaskCount(_e.program, _e.status)}
                          </Button>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Box>
                        {/* {renderCaseStatusIcon(
                        _e.case_status,
                        _e.program,
                        _e.color_code,
                        _e.sf_deeplink,
                        _e.enrollment_id
                      )} */}
                      </Box>
                    )}
                  </Box>
                </div>
              )
            })}
        </Grid>
      </Box>
      {isMonitor && (
        <AddMonitorComponent
          open={isMonitor}
          item={{
            title: 'Add monitor',
            label: 'Select monitor',
            monitorData: monitorData,
          }}
          handleDialogClose={handleMonitorDialogClose}
          handleSubmit={handleMonitorSubmit}
          currProgram={clickedProgram}
        />
      )}
      {isLifestyle && (
        <AddLifestyleDialogComponent
          open={isLifestyle}
          item={{
            title: 'Add lifestyle plan',
            label: 'Select plan',
            lifestyleData: lifestyleData,
          }}
          currProgram={clickedProgram}
          handleDialogClose={handleLifestyleDialogClose}
          handleSubmit={handleLifestyleSubmit}
        />
      )}
      {isActionItem && (
        <PendingItemsDialogComponent
          open={isActionItem}
          item={{
            title: getActionTitle(),
            program: clickedProgram,
            actionList: {},
          }}
          handleDialogClose={handleActionDialogClose}
        />
      )}
      {isPendingItem && (
        <OverrideItemsComponent
          open={isPendingItem}
          item={{
            title: getActionTitle(),
            program: clickedProgram,
          }}
          handleDialogClose={handlePendingDialogClose}
        />
      )}
      {prgStatus !== null && (
        <ConfirmationDialogComponent
          open={prgStatus}
          content={`Are you sure to make program ${prgStatus.toLocaleLowerCase()}?`}
          handleConfirmClose={handlePrgStatusConfirmClose}
          confirmStatus={confirmPrgStatus}
        />
      )}
      {removePrgEnrollment && (
        <ConfirmationDialogComponent open={removePrgEnrollment} content={`Are you sure to remove program enrollment?`} handleConfirmClose={handleConfirmClose} confirmStatus={confirmStatus} />
      )}
    </>
  )
}

export default withStyles(stylesheet)(SubHeaderComponent)
